/** */
import React, { useEffect, useState } from 'react';
import { useAnalystService } from 'hooks/api/agents/analyst';
import { GridColumn, GridRow } from 'app/member/layouts/grid';
import { SectionCard } from 'app/shared/section-card';
import { Spacer } from 'app/layouts/generic';
import { fmt } from 'utilities/format';
import { DOMAIN_CARDS } from '../domain-card/config';
import styles from './monthly-report.module.css';

const HEADER_METRICS = [
  { key: 'platform_health_score', label: 'Health score', format: 'count' },
  { key: 'anomaly_count', label: 'Anomalies', format: 'count' },
  { key: 'critical_anomaly_count', label: 'Critical', format: 'count', invert: true },
  { key: 'tenants_at_risk', label: 'At risk', format: 'count', invert: true }
];

const fullLabel = (report) =>
  report
    ? new Date(report.year, report.month - 1).toLocaleString('en-GB', {
        month: 'long',
        year: 'numeric'
      })
    : '—';

const shortLabel = (report) =>
  report
    ? new Date(report.year, report.month - 1).toLocaleString('en-GB', {
        month: 'short',
        year: '2-digit'
      })
    : '—';

const delta = (a, b) => {
  if (a == null || b == null || b === 0) return null;
  return ((a - b) / Math.abs(b)) * 100;
};

const DeltaBadge = ({ a, b, invert = false }) => {
  const pct = delta(a, b);
  if (pct === null || Math.abs(pct) < 0.05) return <span className={styles.deltaFlat}>—</span>;
  const up = pct > 0;
  const positive = invert ? !up : up;
  return (
    <span className={`${styles.delta} ${positive ? styles.deltaPos : styles.deltaNeg}`}>
      {up ? '+' : ''}
      {pct.toFixed(1)}%
    </span>
  );
};

const ColHeader = ({ label_a, label_b }) => (
  <div className={styles.colHeader}>
    <span />
    <span className={styles.colMonthA}>{label_a}</span>
    <span className={styles.colVs}>vs</span>
    <span className={styles.colMonthB}>{label_b}</span>
  </div>
);

const MonthSelect = ({ value, options, onChange, placeholder }) => (
  <div className={styles.pickerWrap}>
    <select className={styles.picker} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map((r) => (
        <option key={r.month_label} value={r.month_label}>
          {new Date(r.year, r.month - 1).toLocaleString('en-GB', {
            month: 'long',
            year: 'numeric'
          })}
        </option>
      ))}
    </select>
  </div>
);

const CompareRow = ({ label, val_a, val_b, format, invert }) => (
  <div className={styles.compareRow}>
    <span className={styles.compareLabel}>{label}</span>
    <span className={styles.compareVal}>{fmt(val_a, format)}</span>
    <DeltaBadge a={val_a} b={val_b} invert={invert} />
    <span className={styles.compareVal}>{fmt(val_b, format)}</span>
  </div>
);

const DomainCompareCard = ({ card, report_a, report_b }) => {
  const metrics_a = report_a?.domains?.[card.key]?.metrics || {};
  const metrics_b = report_b?.domains?.[card.key]?.metrics || {};
  const all_metrics = [card.headline, ...card.supporting];

  return (
    <SectionCard title={card.title}>
      <ColHeader label_a={shortLabel(report_a)} label_b={shortLabel(report_b)} />
      <div className={styles.domainMetrics}>
        {all_metrics.map((m) => (
          <CompareRow
            key={m.metric}
            label={m.label}
            val_a={metrics_a[m.metric]?.value ?? null}
            val_b={metrics_b[m.metric]?.value ?? null}
            format={m.format}
            invert={m.invert}
          />
        ))}
      </div>
    </SectionCard>
  );
};

export const MonthlyReport = () => {
  const { fetchMonthlyReports, fetchMonthlyReport } = useAnalystService();

  const [available, setAvailable] = useState([]);
  const [month_a, setMonthA] = useState('');
  const [month_b, setMonthB] = useState('');
  const [report_a, setReportA] = useState(null);
  const [report_b, setReportB] = useState(null);
  const [loading_a, setLoadingA] = useState(false);
  const [loading_b, setLoadingB] = useState(false);

  useEffect(() => {
    fetchMonthlyReports({ query_string: 'status=complete' }).then(({ reports }) => {
      const sorted = [...reports].sort((a, b) => b.month_label.localeCompare(a.month_label));
      setAvailable(sorted);
      if (sorted.length >= 1) setMonthA(sorted[0].month_label);
      if (sorted.length >= 2) setMonthB(sorted[1].month_label);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!month_a) {
      setReportA(null);
      return;
    }
    const [year, month] = month_a.split('-');
    setLoadingA(true);
    fetchMonthlyReport(year, month).then(({ report }) => {
      setReportA(report);
      setLoadingA(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month_a]);

  useEffect(() => {
    if (!month_b) {
      setReportB(null);
      return;
    }
    const [year, month] = month_b.split('-');
    setLoadingB(true);
    fetchMonthlyReport(year, month).then(({ report }) => {
      setReportB(report);
      setLoadingB(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month_b]);

  const loading = loading_a || loading_b;

  return (
    <>
      <div className={styles.pickerBar}>
        <MonthSelect
          value={month_a}
          options={available}
          onChange={setMonthA}
          placeholder="Select month A"
        />
        <span className={styles.vs}>vs</span>
        <MonthSelect
          value={month_b}
          options={available}
          onChange={setMonthB}
          placeholder="Select month B"
        />
        {loading && <span className={styles.loadingHint}>Loading…</span>}
      </div>

      {(report_a || report_b) && (
        <>
          <Spacer multiple={4} />

          <SectionCard>
            <ColHeader label_a={fullLabel(report_a)} label_b={fullLabel(report_b)} />
            {HEADER_METRICS.map((m) => (
              <CompareRow
                key={m.key}
                label={m.label}
                val_a={report_a?.[m.key] ?? null}
                val_b={report_b?.[m.key] ?? null}
                format={m.format}
                invert={m.invert}
              />
            ))}
          </SectionCard>

          <Spacer multiple={4} />

          <GridRow num_of_columns={3} auto_flow={false}>
            {DOMAIN_CARDS.map((card) => (
              <GridColumn key={card.key} span={1} auto_columns={false}>
                <DomainCompareCard card={card} report_a={report_a} report_b={report_b} />
              </GridColumn>
            ))}
          </GridRow>
        </>
      )}

      {!report_a && !report_b && !loading && available.length === 0 && (
        <div className={styles.empty}>No monthly reports available yet.</div>
      )}
    </>
  );
};
